import { useCallback, useEffect, useState, useMemo } from "react";
import { FilterOption } from "../../interfaces/FilterOption";
import LanguagesService from "../../services/languages.service";
import { useErrorBoundary } from "react-error-boundary";
import {
  Box,
  Modal,
  Typography,
  Paper,
  Button,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import {
  Translate as TranslateIcon,
  Edit as EditIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import VariationTipsService from "../../services/variation-tips.service";
import useUser from "../../hooks/useUser";
import classes from "./TipVariationsModal.module.scss";
import { useNavigate } from "react-router-dom";
import { RouteNames } from "../../enums/RouteNames";
import ModalLoading from "../../views/modal-loading/ModalLoading";
import { TipVariationsModalProps } from "../../interfaces/props/TipVariationModalProps";
import { VariationTipTypes } from "../../enums/VariationTipTypes";
import { OddsFormat } from "../../enums/OddsFormat";

const MESSAGE = "We are creating your variation.";
const INFO_MESSAGE = "This will take around 1 min, please wait.";
const DIRECT_TRANSLATION_DESCRIPTION = "Keep original meaning";
const REPHRASE_DESCRIPTION = "Adaptive translation with more context";

const TipVariationsModal = ({
  isOpen,
  onClose,
  tip,
}: TipVariationsModalProps) => {
  const [languages, setLanguages] = useState<FilterOption[]>([]);
  const [language, setLanguage] = useState<FilterOption | null>(null);
  const [isLoadingModalOpen, setIsLoadingModalOpen] = useState<boolean>(false);
  const [variationType, setVariationType] = useState<VariationTipTypes>(VariationTipTypes.DIRECT_TRANSLATION);
  const [languageSearchValue, setLanguageSearchValue] = useState<string>("");
  const [oddsFormat, setOddsFormat] = useState<OddsFormat>(OddsFormat.DECIMAL);
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const user = useUser();

  const filteredLanguages = useMemo(() => {
    if (!languageSearchValue) return languages;
    return languages.filter(lang =>
      lang.name.toLowerCase().includes(languageSearchValue.toLowerCase())
    );
  }, [languages, languageSearchValue]);

  const currentLanguage = tip?.attributes?.language?.data?.attributes?.name || "Unknown";

  const isSameLanguageDirectTranslation = useMemo(() => {
    return variationType === VariationTipTypes.DIRECT_TRANSLATION &&
      language &&
      language.name === currentLanguage;
  }, [variationType, language, currentLanguage]);

  const isCreateButtonDisabled = useMemo(() => {
    return !language || !user?.email || !!isSameLanguageDirectTranslation;
  }, [language, user?.email, isSameLanguageDirectTranslation]);

  const onLanguageValueChangeHandler = useCallback((
    event: React.SyntheticEvent<Element, Event>,
    value: FilterOption | null
  ) => {
    setLanguage(value);
  }, []);

  const onInputChangeLanguage = useCallback(async (
    event: React.SyntheticEvent<Element, Event>,
    inputValue: string
  ) => {
    setLanguageSearchValue(inputValue);

    if (inputValue.length >= 2) {
      try {
        const languages = await LanguagesService.getAllByParams("name", inputValue);
        setLanguages(prev => {
          const existingIds = new Set(prev.map(lang => lang.id));
          const newLanguages = languages.filter(lang => !existingIds.has(lang.id));

          return [...prev, ...newLanguages];
        });
      } catch (err) {
        showBoundary(err);
      }
    }
  }, [showBoundary]);

  const onVariationTypeChangeHandler = useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      newType: VariationTipTypes | null
    ) => {
      if (newType !== null) {
        setVariationType(newType);
      }
    },
    []
  );

  const onOddsFormatChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setOddsFormat(event.target.value as OddsFormat);
    },
    []
  );

  const onCreateVariationHandler = useCallback(() => {
    if (!language || !tip?.attributes?.language?.data || !user?.email) {
      return;
    }

    const postBody = {
      data: {
        tipId: tip.id,
        userEmail: user.email,
        originalLanguage: tip.attributes.language.data.attributes.name,
        targetLanguageId: language.id,
        type: variationType,
        oddsFormat: oddsFormat,
      },
    };

    setIsLoadingModalOpen(true);
    onClose();

    VariationTipsService.create(postBody)
      .then((variationTip) => {
        navigate(`${RouteNames.SINGLE_TIP_VARIATION}/${variationTip.id}`);
      })
      .catch((error: Error) => {
        setIsLoadingModalOpen(false);
        showBoundary(error);
      });
  }, [
    language,
    tip,
    user?.email,
    variationType,
    oddsFormat,
    onClose,
    navigate,
    showBoundary,
  ]);

  useEffect(() => {
    if (isOpen) {
      setLanguage(null);
      setLanguageSearchValue("");
      setVariationType(VariationTipTypes.DIRECT_TRANSLATION);
      setOddsFormat(OddsFormat.DECIMAL);

      LanguagesService.getAllByParams()
        .then(setLanguages)
        .catch(showBoundary);
    }
  }, [isOpen, showBoundary]);

  if (isLoadingModalOpen) {
    return (
      <Box className="disable-mouse-click">
        <ModalLoading message={MESSAGE} infoMessage={INFO_MESSAGE} />
      </Box>
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className='d-flex align-items-center justify-content-center p-4'
    >
      <Paper className={classes.modalPaper}>
        <Box className='p-3'>
          <Box className="d-flex align-items-center gap-2">
            <LanguageIcon color="success" />
            <Typography variant="h5" fontWeight="bold">
              Create Tip Variation
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Generate a new version of your tip in a different language or style
          </Typography>
        </Box>

        <Divider />

        <Box className={`${classes.currentLanguageInfo} ps-3 py-2 mb-2`}>
          <Typography variant="subtitle2" color="text.secondary">
            Current Language:
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {currentLanguage}
          </Typography>
        </Box>

        <Box className="px-3 py-2">
          <Typography variant="h6" gutterBottom>
            Choose Odds Format
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              color="success"
              value={oddsFormat}
              onChange={onOddsFormatChangeHandler}
              className={classes.oddsFormatGroup}
            >
              <FormControlLabel
                value={OddsFormat.DECIMAL}
                control={<Radio classes={{ root: classes.radio }} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Decimal Odds
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      e.g., 2.50, 1.80 (European format)
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={OddsFormat.AMERICAN}
                control={<Radio classes={{ root: classes.radio }} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      American Odds
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      e.g., +150, -200 (US format)
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={OddsFormat.FRACTIONAL}
                control={<Radio classes={{ root: classes.radio }} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Fractional Odds
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      e.g., 3/2, 4/5 (UK format)
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box className="px-3 py-2">
          <Typography variant="h6" gutterBottom>
            Target Language
          </Typography>
          <Autocomplete
            className={`${classes.autocomplete} mt-2`}
            classes={{ listbox: `${classes.listboxHeight} scrollbar` }}
            options={filteredLanguages}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search and select target language..."
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  classes: {
                    notchedOutline: classes.borderAutocomplete
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} className={`${classes.option} px-2 py-2`}>
                <Typography variant="body2">
                  {option.name}
                </Typography>
              </Box>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={onLanguageValueChangeHandler}
            value={language}
            onInputChange={onInputChangeLanguage}
            noOptionsText="No languages found"
            loadingText="Searching languages..."
          />
        </Box>

        <Box className='px-3 py-2'>
          <Typography variant="h6" gutterBottom>
            Variation Type
          </Typography>
          <ToggleButtonGroup
            value={variationType}
            exclusive
            onChange={onVariationTypeChangeHandler}
            className={`${classes.toggleGroup} mb-2`}
            fullWidth
          >
            <ToggleButton
              value="direct-translation"
              className={`${classes.toggleButton} d-flex flex-column align-items-start px-3 py-2`}
            >
              <TranslateIcon className="me-2" />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Direct Translation
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {DIRECT_TRANSLATION_DESCRIPTION}
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton
              value="rephrase"
              className={`${classes.toggleButton} d-flex flex-column align-items-start px-3 py-2`}
            >
              <EditIcon className="me-2" />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Rephrase
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {REPHRASE_DESCRIPTION}
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        <Box className='d-flex justify-content-end gap-2 pe-3 py-3'>
          <Button
            onClick={onClose}
            variant="outlined"
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Tooltip
            title={isSameLanguageDirectTranslation
              ? `Can't create direct translation for the same language`
              : ""
            }
            placement="top"
          >
            <Box>
              <Button
                onClick={onCreateVariationHandler}
                disabled={isCreateButtonDisabled}
                variant="contained"
                className={classes.createButton}
              >
                Create Variation
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </Paper>
    </Modal>
  );
};

export default TipVariationsModal;
